import { createSelector } from 'reselect';

import { initialState } from './reducer';
import makeSelectMain, {
  getError as getMainError,
  selectMainStatus,
} from '../Main/selectors';
import {
  CONSENT_MOBILE_PATH,
  CONSENT_NAMES_TO_SEND,
  CONSENT_PAGE_HEADER_RECONSENT,
  CONSENT_SECONDARY_BUTTON_LABEL,
  CONSENT_SUBMIT_LABEL,
  FORM_CONSENT,
  STATUS_CONSENT,
} from '../Main/constants';

/**
 * Direct selector to the Login state domain
 */
const selectLoginDomain = (state) => state.login || initialState;

/**
 * Direct selector to Login props
 * This selector can be used to grab React Router props that maybe passed down
 */
const selectLoginProps = (state, props) => props;

/**
 * Other specific selectors
 */

/**
 * @description Selects consents user agreed to
 */
const getConsentsAgreed = () => createSelector(
  getLoginForm(FORM_CONSENT),
  (inputs) => Object.keys(inputs)
    .filter((key) => inputs[key])
    .map((consent) => CONSENT_NAMES_TO_SEND[consent])
    .join(','),
);

/**
 * @description Selects props for the consent form for login (reconsent) flow
 */
const getLoginConsentFormProps = () => createSelector(
  getConsentsAgreed(),
  makeSelectLogin('consentsNeeded'),
  getMainError('main'),
  selectMainStatus(STATUS_CONSENT),
  makeSelectMain('success'),
  getLoginForm(FORM_CONSENT),
  (
    consentsAgreed,
    consentsNeeded,
    error,
    status,
    success,
    formValues,
  ) => ({
    consentsAgreed,
    consentsNeeded,
    error: error || '',
    formName: FORM_CONSENT,
    formValues,
    mobilePath: CONSENT_MOBILE_PATH,
    pageHeader: CONSENT_PAGE_HEADER_RECONSENT,
    secondaryButtonLabel: CONSENT_SECONDARY_BUTTON_LABEL,
    status,
    submitLabel: CONSENT_SUBMIT_LABEL,
    success,
  }),
);

/**
 * @description Selects all inputs for a given prop
 */
const getLoginForm = (prop) => createSelector(
  makeSelectLogin('forms'),
  (forms) => forms[prop],
);

/**
 * @description Selects errors and formatted form input errors for screen reader
 */
const getLoginNotifications = () => createSelector(
  getMainError('main'),
  makeSelectMain('success'),
  getPasscodeValidationError(),
  (error, success, passcodeError) => {
    const notifications = [];
    if (error) { notifications.push(error); }
    if (success) { notifications.push(success); }
    if (passcodeError) { notifications.push(passcodeError); }
    return notifications;
  },
);

const getLoginPinDrawerNotifications = () => createSelector(
  getMainError('pin'),
  (error) => error ? [error] : [],
);

/**
 * @description Selects view
 */
const getPageView = () => createSelector(
  selectLoginProps,
  (props) => props.match.params.view || 'main',
);

/**
 * @description Selects passcode
 */
const getPasscode = () => createSelector(
  getLoginForm('totp'),
  (inputs) => inputs.passcode,
);

const getPasscodeValidationError = () => createSelector(
  selectLoginDomain,
  (state) => state.formErrors.totp.passcode,
);

/**
 * @description Selects password
 */
const getPassword = () => createSelector(
  getLoginForm('login'),
  (inputs) => inputs.password,
);

const getPin = () => createSelector(
  getLoginForm('pin'),
  (pinForm) => pinForm.pin,
);

/**
 * @description Selects username
 */
const getUsername = () => createSelector(
  getLoginForm('login'),
  (inputs) => inputs.username,
);

/**
 * @description Selects the Welcome screen form errors
 */
const getWelcomeValidationErrors = () => createSelector(
  makeSelectLogin('formErrors'),
  (formErrors) => formErrors.welcome,
);

/**
 * @description Selects the preferred MFA option
 */
const getSelectedMfaOption = () => createSelector(
  getLoginForm('mfa'),
  (inputs) => inputs.selectedOption,
);

const selectInputFocusState = (field) => createSelector(
  makeSelectLogin('focus'),
  (focus) => focus[field],
);

/**
 * Default selector used by Login
 */
const makeSelectLogin = (prop) => createSelector(
  selectLoginDomain,
  (state) => state[prop],
);

export default makeSelectLogin;
export {
  getConsentsAgreed,
  getLoginForm,
  getLoginConsentFormProps,
  getLoginNotifications,
  getLoginPinDrawerNotifications,
  getPageView,
  getPasscode,
  getPasscodeValidationError,
  getPassword,
  getPin,
  getSelectedMfaOption,
  getUsername,
  getWelcomeValidationErrors,
  selectInputFocusState,
  selectLoginProps,
};
